import React, { useMemo, useCallback, useState, memo } from 'react';
import { X, FileText } from 'lucide-react';
import { getFileUrl } from '../../../api/files';
import ImageViewer from '../../common/ImageViewer';

import styles from './styles/FileAttachment.module.css';

const FileAttachment = ({ file, onRemove, uploadProgress, id, readOnly = false }) => {
    const [showImageViewer, setShowImageViewer] = useState(false);
    const isImage = file?.type?.startsWith('image/');
    const isUploading = useMemo(() => {
        return uploadProgress !== undefined && uploadProgress < 100;
    }, [uploadProgress]);

    const fileExtension = useMemo(() => {
        return file?.type?.split('/')?.[1] || '';
    }, [file]);

    const previewUrl = useMemo(() => {
        if (!isImage) return null;

        if (file instanceof File) {
            return URL.createObjectURL(file);
        }

        return getFileUrl(file.url);
    }, [file, isImage]);

    const renderProgress = useCallback(() => {
        if (!isUploading) return null;

        // Calculate circle properties for progress
        const radius = 25 / 2;
        const circumference = 2 * Math.PI * radius;
        const strokeDashoffset = circumference - (uploadProgress / 100) * circumference;

        return (
            <div className={styles.circularProgress}>
                <svg className={styles.circularProgressSvg} viewBox="0 0 40 40">
                    <circle className={styles.circularProgressBackground} cx="20" cy="20" r={radius} />
                    <circle className={styles.circularProgressBar} cx="20" cy="20" r={radius} strokeDasharray={circumference} strokeDashoffset={strokeDashoffset} />
                </svg>
            </div>
        );
    }, [isUploading, uploadProgress]);

    const handleImageClick = useCallback(() => {
        if (previewUrl) {
            setShowImageViewer(true);
        }
    }, [previewUrl]);

    return (
        <>
            <div className={styles.fileAttachment}>
                {isImage ? (
                    <div 
                        className={styles.imageContainer}
                        onClick={handleImageClick}
                        style={{ cursor: previewUrl ? 'pointer' : 'default' }}
                    >
                        <div
                            className={styles.imagePreview}
                            style={{ backgroundImage: previewUrl ? `url(${previewUrl})` : 'none' }}
                        />
                        {renderProgress()}
                    </div>
                ) : (
                    <div className={styles.fileContainer}>
                        <div className={styles.fileIcon}>
                            {!isUploading && <FileText size={21} />}
                            {renderProgress()}
                        </div>
                        <div className={styles.fileInfo}>
                            <span className={styles.fileName}>{file?.name || ''}</span>
                            <span className={styles.fileExtension}>
                                {fileExtension}
                            </span>
                        </div>
                    </div>
                )}

                {!readOnly && onRemove && (
                    <button
                        className={styles.removeButton}
                        onClick={() => onRemove(id)}
                        aria-label="Remove file"
                    >
                        <X size={13} />
                    </button>
                )}
            </div>

            {showImageViewer && (
                <ImageViewer
                    src={previewUrl}
                    alt={file.name}
                    onClose={() => setShowImageViewer(false)}
                />
            )}
        </>
    );
};

export default memo(FileAttachment);
